import { useState, useRef } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import {
  Sprout,
  Plus,
  AlertCircle,
  Ruler,
  Activity,
  CheckCircle2,
  X,
  ArrowLeft,
  Camera,
  XCircle,
} from 'lucide-react'
import { getBiomassRecords, recordBiomass, getBiomassModelStatus, getPaddocks, uploadFieldPhoto } from '../../services/api'
import { format, parseISO } from 'date-fns'
import StatCard from '../../components/StatCard'
import clsx from 'clsx'
import { useAuth } from '../../contexts/AuthContext'

const METHOD_LABELS = {
  cut_and_weigh: 'Cut & Weigh',
  rising_plate_meter: 'Rising Plate Meter',
  visual_estimation: 'Visual Estimation',
  calibrated_disc: 'Calibrated Disc',
  lidar: 'LiDAR (Drone)',
}

export default function BiomassLog({ ranchId }) {
  const queryClient = useQueryClient()
  const [showModal, setShowModal] = useState(false)
  const [paddockFilter, setPaddockFilter] = useState('')

  const { data: records, isLoading } = useQuery({
    queryKey: ['biomassRecords', ranchId, paddockFilter],
    queryFn: () => getBiomassRecords(ranchId, paddockFilter ? { paddock_id: paddockFilter } : {}),
  })

  const { data: modelStatus, error: modelStatusError } = useQuery({
    queryKey: ['biomassModelStatus', ranchId],
    queryFn: () => getBiomassModelStatus(ranchId),
    retry: 1,
  })

  const { data: paddocks } = useQuery({
    queryKey: ['paddocks', ranchId],
    queryFn: () => getPaddocks(ranchId),
  })

  const totalMeasurements = records?.length || 0
  const latestRecord = records?.[0]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/land" className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="h-5 w-5 text-gray-500" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Biomass Measurements</h1>
            <p className="text-gray-500 mt-1">Field measurements for pasture estimation calibration</p>
          </div>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="btn bg-green-600 text-white hover:bg-green-700 flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" />
          Record Measurement
        </button>
      </div>

      {/* Calibration status load error — shown above the grid so it doesn't
          distort the three-card layout when the /biomass/model-status endpoint
          is briefly unavailable. Prior to this the query error was swallowed
          and the Calibration Progress card rendered "? more needed" forever. */}
      {modelStatusError && (
        <div className="flex items-center gap-2 px-4 py-2.5 bg-red-50 text-red-700 rounded-lg text-sm border border-red-100">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>Couldn't load calibration status — try again in a moment.</span>
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <StatCard
          title="Total Measurements"
          value={totalMeasurements}
          icon={Ruler}
          color="blue"
        />
        <div className="card p-5">
          <div className="flex items-start justify-between mb-3">
            <div className={clsx(
              'p-2.5 rounded-xl ring-1',
              modelStatusError
                ? 'bg-gray-50 ring-gray-200'
                : modelStatus?.model_ready
                  ? 'bg-green-50 ring-green-100'
                  : 'bg-amber-50 ring-amber-100'
            )}>
              {modelStatusError
                ? <AlertCircle className="h-5 w-5 text-gray-400" />
                : modelStatus?.model_ready
                  ? <CheckCircle2 className="h-5 w-5 text-green-600" />
                  : <Activity className="h-5 w-5 text-amber-600" />
              }
            </div>
          </div>
          <div className="text-2xl font-bold text-gray-900">
            {modelStatusError
              ? '—'
              : modelStatus?.model_ready
                ? 'Ready'
                : `${modelStatus?.measurements_needed || '?'} more needed`}
          </div>
          <div className="text-sm text-earth-500 mt-1.5 font-medium">Calibration Progress</div>
          {!modelStatusError && modelStatus && !modelStatus.model_ready && (
            <div className="mt-2">
              <div className="w-full bg-gray-200 rounded-full h-1.5">
                <div
                  className="bg-amber-500 h-1.5 rounded-full transition-all"
                  style={{ width: `${Math.min(100, (modelStatus.paired_with_ndvi / 20) * 100)}%` }}
                ></div>
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {modelStatus.paired_with_ndvi}/20 linked to satellite data
              </p>
            </div>
          )}
        </div>
        <StatCard
          title="Latest Measurement"
          value={latestRecord ? `${latestRecord.biomass_kg_dm_ha} kg DM/ha` : '—'}
          subtitle={latestRecord ? format(parseISO(latestRecord.measurement_date), 'dd MMM yyyy') : null}
          icon={Sprout}
          color="green"
        />
      </div>

      {/* Filter */}
      <div className="card p-4">
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-gray-700">Paddock:</label>
          <select
            value={paddockFilter}
            onChange={e => setPaddockFilter(e.target.value)}
            className="input max-w-xs"
          >
            <option value="">All paddocks</option>
            {paddocks?.map(p => (
              <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          </div>
        ) : !records?.length ? (
          <div className="text-center py-12">
            <Sprout className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No measurements yet</h3>
            <p className="text-gray-500 mt-1">Record field measurements to improve pasture estimates</p>
            <button
              onClick={() => setShowModal(true)}
              className="btn bg-green-600 text-white hover:bg-green-700 mt-4 inline-flex items-center"
            >
              <Plus className="h-4 w-4 mr-2" />
              Record First Measurement
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paddock</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Biomass</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Method</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Grass Height</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dominant Species</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Flight Linkage</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {records.map((r) => (
                  <tr key={r.id} className={r.out_of_window ? 'bg-amber-50/40 hover:bg-amber-50' : 'hover:bg-gray-50'}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {format(parseISO(r.measurement_date), 'dd MMM yyyy')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-50 text-green-700">
                        {r.paddock_code}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                      {r.biomass_kg_dm_ha != null ? `${r.biomass_kg_dm_ha.toLocaleString()} kg DM/ha` : '\u2014'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {METHOD_LABELS[r.measurement_method] || r.measurement_method}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                      {r.grass_height_cm ? `${r.grass_height_cm} cm` : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {r.dominant_species || '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-xs">
                      {r.linked_drone_run_id ? (
                        <span className={r.out_of_window
                          ? 'inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-800'
                          : 'inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-800'}>
                          {r.linkage_offset_days >= 0 ? '+' : ''}{r.linkage_offset_days}d
                          {r.out_of_window ? ' • out of window' : ''}
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gray-100 text-gray-700">
                          no flight ±3d
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Record Modal */}
      {showModal && (
        <RecordBiomassModal
          ranchId={ranchId}
          paddocks={paddocks || []}
          onClose={() => setShowModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['biomassRecords'] })
            queryClient.invalidateQueries({ queryKey: ['biomassModelStatus'] })
            setShowModal(false)
          }}
        />
      )}
    </div>
  )
}

function RecordBiomassModal({ ranchId, paddocks, onClose, onSuccess }) {
  const { userId } = useAuth()
  const today = new Date().toISOString().split('T')[0]
  const [formData, setFormData] = useState({
    paddock_id: '',
    measurement_date: today,
    biomass_kg_dm_ha: '',
    measurement_method: 'cut_and_weigh',
    grass_height_cm: '',
    dominant_species: '',
    notes: '',
    dry_weight_g: '',
    quadrat_area_m2: '0.25',
  })
  const [error, setError] = useState('')
  const [photoUrls, setPhotoUrls] = useState([])
  const [uploadingPhoto, setUploadingPhoto] = useState(false)
  const fileInputRef = useRef(null)

  const isCutAndWeigh = formData.measurement_method === 'cut_and_weigh'

  // Live preview of computed biomass for cut-and-weigh
  const computedBiomass = isCutAndWeigh && formData.dry_weight_g && formData.quadrat_area_m2
    ? Math.round((parseFloat(formData.dry_weight_g) / 1000 / parseFloat(formData.quadrat_area_m2)) * 10000)
    : null

  const mutation = useMutation({
    mutationFn: recordBiomass,
    onSuccess,
    onError: (err) => setError(err.response?.data?.detail || 'Failed to record measurement'),
  })

  const handlePhotoSelect = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadingPhoto(true)
    try {
      const result = await uploadFieldPhoto(file, 'biomass')
      setPhotoUrls(prev => [...prev, result.url])
    } catch {
      setError('Failed to upload photo')
    } finally {
      setUploadingPhoto(false)
      if (fileInputRef.current) fileInputRef.current.value = ''
    }
  }

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    setError('')

    if (!formData.paddock_id) {
      setError('Select a paddock')
      return
    }

    if (isCutAndWeigh) {
      if (!formData.dry_weight_g || parseFloat(formData.dry_weight_g) <= 0) {
        setError('Enter the dry weight from the quadrat')
        return
      }
      if (!formData.quadrat_area_m2 || parseFloat(formData.quadrat_area_m2) <= 0) {
        setError('Enter the quadrat area')
        return
      }
    } else {
      if (!formData.biomass_kg_dm_ha || parseFloat(formData.biomass_kg_dm_ha) <= 0) {
        setError('Enter a valid biomass measurement')
        return
      }
    }

    const payload = {
      ranch_id: ranchId,
      paddock_id: formData.paddock_id,
      measurement_date: formData.measurement_date,
      measurement_method: formData.measurement_method,
      grass_height_cm: formData.grass_height_cm ? parseFloat(formData.grass_height_cm) : null,
      dominant_species: formData.dominant_species || null,
      notes: formData.notes || null,
      photo_urls: photoUrls.length ? photoUrls : [],
      measured_by: userId,
    }

    if (isCutAndWeigh) {
      payload.dry_weight_g = parseFloat(formData.dry_weight_g)
      payload.quadrat_area_m2 = parseFloat(formData.quadrat_area_m2)
    } else {
      payload.biomass_kg_dm_ha = parseFloat(formData.biomass_kg_dm_ha)
    }

    mutation.mutate(payload)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Record Biomass Measurement</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Paddock *</label>
              <select
                value={formData.paddock_id}
                onChange={e => handleChange('paddock_id', e.target.value)}
                className="input w-full"
                required
              >
                <option value="">Select paddock</option>
                {paddocks.map(p => (
                  <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="label">Date *</label>
              <input
                type="date"
                value={formData.measurement_date}
                onChange={e => handleChange('measurement_date', e.target.value)}
                className="input w-full"
                required
              />
            </div>
          </div>

          <div>
            <label className="label">Method *</label>
            <select
              value={formData.measurement_method}
              onChange={e => handleChange('measurement_method', e.target.value)}
              className="input w-full"
            >
              {Object.entries(METHOD_LABELS).map(([k, v]) => (
                <option key={k} value={k}>{v}</option>
              ))}
            </select>
          </div>

          {isCutAndWeigh ? (
            <>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="label">Dry Weight (g) *</label>
                  <input
                    type="number"
                    value={formData.dry_weight_g}
                    onChange={e => handleChange('dry_weight_g', e.target.value)}
                    placeholder="e.g. 150"
                    className="input w-full"
                    min="0"
                    step="0.1"
                    required
                  />
                </div>
                <div>
                  <label className="label">Quadrat Area (m²) *</label>
                  <input
                    type="number"
                    value={formData.quadrat_area_m2}
                    onChange={e => handleChange('quadrat_area_m2', e.target.value)}
                    placeholder="0.25"
                    className="input w-full"
                    min="0.01"
                    step="0.01"
                    required
                  />
                </div>
              </div>
              {computedBiomass != null && (
                <div className="text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
                  = <span className="font-semibold text-gray-900">{computedBiomass.toLocaleString()}</span> kg DM/ha
                </div>
              )}
            </>
          ) : (
            <div>
              <label className="label">Biomass (kg DM/ha) *</label>
              <input
                type="number"
                value={formData.biomass_kg_dm_ha}
                onChange={e => handleChange('biomass_kg_dm_ha', e.target.value)}
                placeholder="e.g. 2500"
                className="input w-full"
                min="0"
                step="10"
                required
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="label">Grass Height (cm)</label>
              <input
                type="number"
                value={formData.grass_height_cm}
                onChange={e => handleChange('grass_height_cm', e.target.value)}
                placeholder="e.g. 35"
                className="input w-full"
                min="0"
                step="0.5"
              />
            </div>
            <div>
              <label className="label">Dominant Species</label>
              <input
                type="text"
                value={formData.dominant_species}
                onChange={e => handleChange('dominant_species', e.target.value)}
                placeholder="e.g. Brachiaria, Chloris"
                className="input w-full"
              />
            </div>
          </div>

          <div>
            <label className="label">Notes</label>
            <textarea
              value={formData.notes}
              onChange={e => handleChange('notes', e.target.value)}
              placeholder="Any observations about the measurement site..."
              className="input w-full"
              rows={2}
            />
          </div>

          {/* Photo Upload */}
          <div>
            <label className="label">Photos</label>
            {photoUrls.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {photoUrls.map((url, i) => (
                  <div key={i} className="relative group">
                    <img src={url} alt={`Photo ${i + 1}`} className="h-16 w-16 object-cover rounded-lg border" />
                    <button
                      type="button"
                      onClick={() => setPhotoUrls(prev => prev.filter((_, j) => j !== i))}
                      className="absolute -top-1.5 -right-1.5 hidden group-hover:block"
                    >
                      <XCircle className="h-4 w-4 text-red-500 bg-white rounded-full" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploadingPhoto}
              className="btn bg-gray-50 text-gray-700 border border-gray-300 hover:bg-gray-100 text-sm flex items-center gap-2"
            >
              {uploadingPhoto ? (
                <>
                  <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-gray-600"></div>
                  Uploading...
                </>
              ) : (
                <>
                  <Camera className="h-3.5 w-3.5" />
                  {photoUrls.length ? 'Add Another Photo' : 'Take Photo'}
                </>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              capture="environment"
              onChange={handlePhotoSelect}
              className="hidden"
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="btn bg-gray-100 text-gray-700 hover:bg-gray-200">
              Cancel
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="btn bg-green-600 text-white hover:bg-green-700 flex items-center gap-2"
            >
              {mutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  Saving...
                </>
              ) : (
                <>
                  <Sprout className="h-4 w-4" />
                  Save Measurement
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
